import { Typography } from '@occc-app/design-system';

import {
  DateTimeFormat,
  DateTimeFormatTZ,
  formatDateTime,
  formatDateTimeTZ,
  getUserTimezone,
  isDateTimeValid,
  parseDate,
} from './date';

export const bytesToMB = (bytes: number) => bytes / 1048576;

export const numberTOOrdinal = (n: number) => {
  const suffixes = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (suffixes[(v - 20) % 10] || suffixes[v] || suffixes[0]);
};

export const readableDate = (
  date: Date | string | undefined | null,
  format: DateTimeFormat = DateTimeFormat.DateWithYear,
) => {
  if (date && isDateTimeValid(parseDate(date))) {
    return formatDateTime(parseDate(date), format);
  }

  return '';
};

export const readableTimezone = (
  timeZoneFormat: DateTimeFormatTZ = DateTimeFormatTZ.ShortTimeZoneWithOffset,
  withoutIANA?: boolean,
) => {
  const timeZone = getUserTimezone();
  // eslint-disable-next-line
  return (
    formatDateTimeTZ(new Date(), timeZone, timeZoneFormat) + (withoutIANA ? '' : ` ${timeZone}`)
  );
};

export const readableDateWithTimezone = (
  date: Date | string | undefined | null,
  format: DateTimeFormat = DateTimeFormat.DateWithYear,
  timeZoneFormat?: DateTimeFormatTZ,
  withoutIANA?: boolean,
) => {
  const formattedDate = readableDate(date, format);

  if (formattedDate) {
    const timezone = readableTimezone(timeZoneFormat, withoutIANA);
    return `${formattedDate} ${timezone}`;
  }

  return '';
};

export const readableDateWithShortTimezone = (
  date: Date | string | undefined | null,
  format: DateTimeFormat = DateTimeFormat.DateTimeFullMonth12h,
) => {
  return readableDateWithTimezone(date, format, DateTimeFormatTZ.ShortTimeZone, true);
};

export const readableNumber = (num?: number) => {
  if (!num) {
    return '0';
  }
  const roundedNum = Math.round(num * 100) / 100;
  const parts = roundedNum.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

// TODO: fix later
export const uuid = (id: string) => (id ? id : <Typography component='span'>None</Typography>);

export const uuidRegex = '[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}';

type UUIDRegExpParams = { pre?: string; post?: string; options?: string };
type UUIDRegExp = (params: UUIDRegExpParams) => RegExp;
export const uuidRegExp: UUIDRegExp = ({ pre = '', post = '', options = '' }) => {
  return new RegExp(`${pre}${uuidRegex}${post}`, options);
};

export const FULL_DATE_FORMAT = DateTimeFormat.DateTime12h;

type TIME_FORMATS =
  | DateTimeFormat.Time24h
  | DateTimeFormat.Time24hShort
  | DateTimeFormat.Time12h
  | DateTimeFormat.Time12hShort;

export const formatTime = (time: string | Date, format: TIME_FORMATS = DateTimeFormat.Time24h) => {
  return formatDateTime(parseDate(time), format);
};
